import { useCallback, useState } from 'react';

// Per-user persistence for Grid/List toggles. List is the default for
// new visitors (matches NextCentury's onboarding ergonomics); a
// returning user's choice sticks across sessions via localStorage.
// Safari private mode throws on access — every read/write is try/catch'd
// so the hook is degraded-but-functional there.
//
// May 15 batch: takes a `storageKey` argument so the Property Portal
// (norgas.propertyPortal.deviceViewMode) and the /properties index
// (norgas.properties.viewMode) can use independent preferences without
// stepping on each other. Also writes synchronously inside the setter
// instead of via a useEffect — the previous effect-based persistence
// could race a fast SPA navigation, leaving localStorage stale.
export type ViewMode = 'grid' | 'list';

function readStored(key: string, defaultMode: ViewMode): ViewMode {
  if (typeof window === 'undefined') return defaultMode;
  try {
    const stored = window.localStorage.getItem(key);
    return stored === 'grid' || stored === 'list' ? stored : defaultMode;
  } catch {
    return defaultMode;
  }
}

export function useViewMode(
  storageKey: string,
  defaultMode: ViewMode = 'list',
): [ViewMode, (m: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(() => readStored(storageKey, defaultMode));

  const setModeSynced = useCallback((next: ViewMode) => {
    setMode(next);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, next);
    } catch {
      // Safari private mode / quota errors — ignore; in-memory state still works.
    }
  }, [storageKey]);

  return [mode, setModeSynced];
}
